// Sprint 12 — Redirects list + create/edit/delete + toggle-active.
//
// Responsive: AntD Grid.useBreakpoint — desktop renders the table, mobile
// renders a card list. RBAC: read for everyone with WEB_REDIRECT permission,
// write actions also require WEB_REDIRECT (BE enforces).

import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Empty,
  Form,
  Grid,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { ArrowRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@shared/hooks/useDebounce';
import { DataTable } from '@shared/components/DataTable';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { webApi } from '@api/web';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import {
  REDIRECT_STATUS_CODES,
  REDIRECT_STATUS_COLOR,
  type CreateRedirectInput,
  type RedirectRule,
  type RedirectStatusCode,
} from '../types';

const { Text } = Typography;

interface FormValues {
  fromPath: string;
  toPath: string;
  statusCode: RedirectStatusCode;
  isActive: boolean;
  notes?: string;
}

interface ModalState {
  open: boolean;
  editing: RedirectRule | null;
}

// Path validation — must start with `/`, no whitespace.
const PATH_PATTERN = /^\/[^\s]*$/;

type ActiveFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';

function statusTagColor(code: number): string {
  if ((REDIRECT_STATUS_CODES as readonly number[]).includes(code)) {
    return REDIRECT_STATUS_COLOR[code as RedirectStatusCode];
  }
  return 'default';
}

export function RedirectMapPage() {
  const { t } = useTranslation(['web', 'common', 'validation', 'errors']);
  const td = t as unknown as (key: string) => string;
  const qc = useQueryClient();
  const canEdit = useCan(PERMISSIONS.WEB_REDIRECT);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [modal, setModal] = useState<ModalState>({ open: false, editing: null });
  const [form] = Form.useForm<FormValues>();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('ALL');

  const isActiveParam =
    activeFilter === 'ACTIVE' ? true : activeFilter === 'INACTIVE' ? false : undefined;

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ['web-redirects', { page, pageSize, search: debouncedSearch, activeFilter }],
    queryFn: () =>
      webApi.redirects.list({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        isActive: isActiveParam,
      }),
    placeholderData: (prev) => prev,
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateRedirectInput) => webApi.redirects.create(input),
    onSuccess: async (row) => {
      toast.success(t('redirect.toast.created'));
      await qc.invalidateQueries({ queryKey: ['web-redirects'] });
      void recordAuditAction({
        action: 'web.redirect.create',
        resource: 'webRedirect',
        resourceId: row.id,
        summary: `${row.fromPath} → ${row.toPath} (${row.statusCode})`,
      });
      setModal({ open: false, editing: null });
      form.resetFields();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      input,
      rowVersion,
    }: {
      id: string;
      input: CreateRedirectInput;
      rowVersion: string | null;
    }) => webApi.redirects.update(id, input, rowVersion),
    onSuccess: async (row) => {
      toast.success(t('redirect.toast.updated'));
      await qc.invalidateQueries({ queryKey: ['web-redirects'] });
      void recordAuditAction({
        action: 'web.redirect.update',
        resource: 'webRedirect',
        resourceId: row.id,
        summary: `${row.fromPath} → ${row.toPath} (${row.statusCode})`,
      });
      setModal({ open: false, editing: null });
      form.resetFields();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (row: RedirectRule) => webApi.redirects.toggleActive(row.id),
    onSuccess: async (_d, row) => {
      await qc.invalidateQueries({ queryKey: ['web-redirects'] });
      void recordAuditAction({
        action: 'web.redirect.update',
        resource: 'webRedirect',
        resourceId: row.id,
        summary: `${row.fromPath} ${row.isActive ? 'pasifleştirildi' : 'aktifleştirildi'}`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (row: RedirectRule) => webApi.redirects.remove(row.id),
    onSuccess: async (_d, row) => {
      toast.success(t('redirect.toast.deleted'));
      await qc.invalidateQueries({ queryKey: ['web-redirects'] });
      void recordAuditAction({
        action: 'web.redirect.delete',
        resource: 'webRedirect',
        resourceId: row.id,
        summary: `${row.fromPath} → ${row.toPath} silindi`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      fromPath: '',
      toPath: '',
      statusCode: 301,
      isActive: true,
      notes: '',
    });
    setModal({ open: true, editing: null });
  };

  const openEdit = (row: RedirectRule) => {
    form.setFieldsValue({
      fromPath: row.fromPath,
      toPath: row.toPath,
      statusCode: (REDIRECT_STATUS_CODES as readonly number[]).includes(row.statusCode)
        ? (row.statusCode as RedirectStatusCode)
        : 301,
      isActive: row.isActive,
      notes: row.notes ?? '',
    });
    setModal({ open: true, editing: row });
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: CreateRedirectInput = {
        fromPath: values.fromPath,
        toPath: values.toPath,
        statusCode: values.statusCode,
        isActive: values.isActive,
        notes: values.notes && values.notes.trim() !== '' ? values.notes : null,
      };
      if (modal.editing) {
        updateMutation.mutate({
          id: modal.editing.id,
          input: payload,
          rowVersion: modal.editing.rowVersion,
        });
      } else {
        createMutation.mutate(payload);
      }
    } catch {
      // surface inline
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'fromPath',
        title: t('redirect.table.fromPath'),
        dataIndex: 'fromPath',
        width: 240,
        render: (v: string) => (
          <code
            style={{
              fontSize: 12,
              background: 'var(--ds-surface-2)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            {v}
          </code>
        ),
      },
      {
        key: 'arrow',
        title: '',
        width: 32,
        render: () => (
          <ArrowRight size={14} style={{ color: 'var(--ds-text-tertiary)' }} />
        ),
      },
      {
        key: 'toPath',
        title: t('redirect.table.toPath'),
        dataIndex: 'toPath',
        width: 240,
        render: (v: string) => (
          <code
            style={{
              fontSize: 12,
              background: 'var(--ds-surface-2)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            {v}
          </code>
        ),
      },
      {
        key: 'statusCode',
        title: t('redirect.table.statusCode'),
        dataIndex: 'statusCode',
        width: 90,
        render: (v: number) => <Tag color={statusTagColor(v)}>{v}</Tag>,
      },
      {
        key: 'hitCount',
        title: t('redirect.table.hitCount'),
        dataIndex: 'hitCount',
        width: 80,
        align: 'right' as const,
        render: (v: number) => (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>
        ),
      },
      {
        key: 'notes',
        title: t('redirect.table.notes'),
        dataIndex: 'notes',
        ellipsis: true,
        render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
      },
      {
        key: 'updatedAt',
        title: t('redirect.table.updatedAt'),
        dataIndex: 'updatedAt',
        width: 120,
        render: (v: string) => (
          <span style={{ fontSize: 12 }}>{dayjs(v).format('DD.MM.YYYY')}</span>
        ),
      },
      {
        key: 'isActive',
        title: t('redirect.table.isActive'),
        dataIndex: 'isActive',
        width: 80,
        render: (v: boolean, row: RedirectRule) => (
          <Switch
            size="small"
            checked={v}
            disabled={!canEdit || toggleMutation.isPending}
            onChange={() => toggleMutation.mutate(row)}
          />
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 110,
        render: (_: unknown, row: RedirectRule) => (
          <Space>
            {canEdit ? (
              <Button
                size="small"
                icon={<Pencil size={14} />}
                onClick={() => openEdit(row)}
                aria-label={t('common:actions.edit')}
              />
            ) : null}
            {canEdit ? (
              <Popconfirm
                title={t('redirect.delete.title')}
                description={t('redirect.delete.description', { path: row.fromPath })}
                okText={t('common:actions.delete')}
                okButtonProps={{ danger: true }}
                cancelText={t('common:actions.cancel')}
                onConfirm={() => deleteMutation.mutate(row)}
              >
                <Button
                  size="small"
                  danger
                  icon={<Trash2 size={14} />}
                  aria-label={t('common:actions.delete')}
                />
              </Popconfirm>
            ) : null}
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, deleteMutation, toggleMutation, t],
  );

  const filterBar = (
    <Space wrap>
      <Segmented<ActiveFilter>
        size="small"
        value={activeFilter}
        onChange={(v) => {
          setActiveFilter(v);
          setPage(1);
        }}
        options={[
          { value: 'ALL', label: t('common:labels.all') },
          { value: 'ACTIVE', label: t('redirect.filter.active') },
          { value: 'INACTIVE', label: t('redirect.filter.inactive') },
        ]}
      />
    </Space>
  );

  return (
    <div>
      <PageHeader
        title={t('redirect.page.title')}
        description={t('redirect.page.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: 'Web' },
          { label: t('redirect.page.breadcrumb') },
        ]}
        actions={
          canEdit ? (
            <Button
              type="primary"
              icon={<Plus size={14} />}
              onClick={openCreate}
              data-testid="redirect-new-btn"
            >
              {t('redirect.newButton')}
            </Button>
          ) : null
        }
      />

      <Card data-testid="redirect-list-card">
        {isMobile ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Input.Search
              placeholder={t('redirect.searchPlaceholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              allowClear
            />
            {filterBar}
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                <Spin />
              </div>
            ) : (data?.items.length ?? 0) === 0 ? (
              <Empty description={t('redirect.empty')} />
            ) : (
              data?.items.map((row) => (
                <Card key={row.id} size="small" bodyStyle={{ padding: 12 }}>
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    <Space size={6} wrap>
                      <Tag color={statusTagColor(row.statusCode)}>{row.statusCode}</Tag>
                      <Tag color={row.isActive ? 'green' : 'default'}>
                        {row.isActive ? t('redirect.tag.active') : t('redirect.tag.inactive')}
                      </Tag>
                    </Space>
                    <div>
                      <code style={{ fontSize: 12 }}>{row.fromPath}</code>
                      <ArrowRight
                        size={12}
                        style={{ margin: '0 6px', color: 'var(--ds-text-tertiary)' }}
                      />
                      <code style={{ fontSize: 12 }}>{row.toPath}</code>
                    </div>
                    {row.notes ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.notes}
                      </Text>
                    ) : null}
                    <Space size={8} style={{ justifyContent: 'flex-end', width: '100%' }}>
                      <Switch
                        size="small"
                        checked={row.isActive}
                        disabled={!canEdit || toggleMutation.isPending}
                        onChange={() => toggleMutation.mutate(row)}
                        aria-label={t('redirect.table.isActive')}
                      />
                      {canEdit ? (
                        <Button
                          size="small"
                          icon={<Pencil size={14} />}
                          onClick={() => openEdit(row)}
                          aria-label={t('common:actions.edit')}
                        />
                      ) : null}
                      {canEdit ? (
                        <Popconfirm
                          title={t('redirect.delete.title')}
                          okText={t('common:actions.delete')}
                          okButtonProps={{ danger: true }}
                          cancelText={t('common:actions.cancel')}
                          onConfirm={() => deleteMutation.mutate(row)}
                        >
                          <Button
                            size="small"
                            danger
                            icon={<Trash2 size={14} />}
                            aria-label={t('common:actions.delete')}
                          />
                        </Popconfirm>
                      ) : null}
                    </Space>
                  </Space>
                </Card>
              ))
            )}
          </Space>
        ) : (
          <DataTable<RedirectRule>
            data={data?.items ?? []}
            columns={columns}
            rowKey="id"
            loading={isFetching}
            exportable={false}
            scrollX={1280}
            search={{
              value: search,
              onChange: (v) => {
                setSearch(v);
                setPage(1);
              },
              placeholder: t('redirect.searchPlaceholder'),
            }}
            filters={filterBar}
            pagination={{
              page,
              size: pageSize,
              total: data?.total ?? 0,
              onChange: (p, s) => {
                setPage(p);
                setPageSize(s);
              },
            }}
          />
        )}
      </Card>

      <Modal
        open={modal.open}
        title={modal.editing ? t('redirect.modal.editTitle') : t('redirect.modal.createTitle')}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={() => {
          void onSubmit();
        }}
        afterOpenChange={(open) => {
          if (!open) form.resetFields();
        }}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        destroyOnClose
      >
        <Form<FormValues> form={form} layout="vertical">
          <Form.Item
            label={t('redirect.form.fromPath')}
            name="fromPath"
            rules={[
              { required: true, message: t('redirect.form.fromPathRequired') },
              {
                pattern: PATH_PATTERN,
                message: t('redirect.form.pathPattern'),
              },
            ]}
          >
            <Input placeholder={t('redirect.form.fromPathPlaceholder')} />
          </Form.Item>
          <Form.Item
            label={t('redirect.form.toPath')}
            name="toPath"
            rules={[
              { required: true, message: t('redirect.form.toPathRequired') },
              {
                pattern: PATH_PATTERN,
                message: t('redirect.form.pathPattern'),
              },
            ]}
          >
            <Input placeholder={t('redirect.form.toPathPlaceholder')} />
          </Form.Item>
          <Form.Item
            label={t('redirect.form.statusCode')}
            name="statusCode"
            rules={[{ required: true, message: t('redirect.form.statusCodeRequired') }]}
          >
            <Select<RedirectStatusCode>
              options={REDIRECT_STATUS_CODES.map((code) => ({
                value: code,
                label: td(`redirect.status.label.${code}`),
              }))}
            />
          </Form.Item>
          <Form.Item label={t('redirect.form.notes')} name="notes">
            <Input.TextArea rows={2} placeholder={t('redirect.form.notesPlaceholder')} />
          </Form.Item>
          <Form.Item label={t('redirect.form.isActive')} name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default RedirectMapPage;
